class ProductListToggle {
    constructor() {
      this.handleResize = this.handleResize.bind(this);
      this.init();
    }
  
    init() {
      this.setupEventListeners();
    }
    
    setupEventListeners() {
      document.querySelectorAll('.multicolumn-card__product-list').forEach(container => {
        const button = container.querySelector('[data-card-product-list-button]');
        const listWrapper = container.querySelector('[data-card-product-list]');
        
        if (!button || !listWrapper) return;
        
        listWrapper.style.height = '0';
        listWrapper.style.overflow = 'hidden';
        
        // Create mobile overlay
        const mobileOverlay = document.createElement('div');
        mobileOverlay.classList.add('product-list-mobile-overlay', 'hidden');
        document.body.appendChild(mobileOverlay);
        
        // Create exit button for mobile
        const exitButton = document.createElement('button');
        exitButton.classList.add('product-list-exit-button', 'hidden');
        exitButton.setAttribute('aria-label', 'Close');
        
        // Add to the product list wrapper when mobile mode is active
        listWrapper.appendChild(exitButton);
        
        // Store references to avoid repeated querySelector calls
        container.productListData = {
          button,
          listWrapper,
          mobileOverlay,
          exitButton
        };
        
        // Set up event handlers
        this.setupButtonHandlers(container);
      });
      
      // Initial setup for resize handler
      window.removeEventListener('resize', this.handleResize);
      window.addEventListener('resize', this.handleResize);
    }
    
    setupButtonHandlers(container) {
      const { button, listWrapper, mobileOverlay, exitButton } = container.productListData;
      
      // Exit button click handler
      exitButton.onclick = () => {
        if (window.innerWidth < 750) {
          this.closeMobileList(button, listWrapper, mobileOverlay, exitButton);
        }
      };
      
      // Close on overlay click
      mobileOverlay.onclick = (e) => {
        if (e.target === mobileOverlay && window.innerWidth < 750) {
          this.closeMobileList(button, listWrapper, mobileOverlay, exitButton);
        }
      };
      
      // Button click handler
      button.onclick = () => {
        if (listWrapper.classList.contains('transitioning')) return;
        
        const isOpen = listWrapper.classList.contains('is-open');
        listWrapper.classList.add('transitioning');
        
        if (window.innerWidth < 750) {
          if (!isOpen) {
            this.openMobileList(button, listWrapper, mobileOverlay, exitButton);
          } else {
            this.closeMobileList(button, listWrapper, mobileOverlay, exitButton);
          }
        } else {
          // Find parent image wrapper
          const parentImage = container.closest('.image-with-products');
          
          if (!isOpen) {
            this.openDesktopList(button, listWrapper, parentImage);
          } else {
            this.closeDesktopList(button, listWrapper);
          }
        }
      };
    }
    
    handleResize() {
      document.querySelectorAll('.multicolumn-card__product-list').forEach(container => {
        if (!container.productListData) return;
        
        const { listWrapper, mobileOverlay, exitButton } = container.productListData;
        
        if (listWrapper.classList.contains('is-open')) {
          if (window.innerWidth < 750) {
            // Switch to mobile view if needed
            listWrapper.classList.add('mobile-view');
            mobileOverlay.classList.remove('hidden');
            exitButton.classList.remove('hidden');
          } else {
            // Switch to desktop view if needed
            listWrapper.classList.remove('mobile-view');
            mobileOverlay.classList.add('hidden');
            exitButton.classList.add('hidden');
            
            // Find parent image wrapper
            const parentImage = container.closest('.multicolumn-card__image-wrapper');
            
            // Reset height for desktop
            this.adjustDesktopHeight(listWrapper, parentImage);
          }
        }
      });
    }
    
    adjustDesktopHeight(listWrapper, parentImage) {
      if (!parentImage) return;
      
      listWrapper.style.height = 'auto';
      const contentHeight = listWrapper.scrollHeight;
      const imageHeight = parentImage.offsetHeight;
      const maxHeight = imageHeight - 100; // Allow room for button
      const finalHeight = Math.min(contentHeight, maxHeight);
      
      listWrapper.style.height = `${finalHeight}px`;
      
      // Enable scrolling if needed
      if (contentHeight > maxHeight) {
        listWrapper.style.overflowY = 'auto';
      } else {
        listWrapper.style.overflowY = 'hidden';
      }
    }
    
    openDesktopList(button, listWrapper, parentImage) {
      if (!parentImage) return;
      
      listWrapper.classList.remove('hidden');
      button.classList.add('active');
      
      // Position the wrapper correctly
      listWrapper.style.top = '70px';
      
      setTimeout(() => {
        // Set height to auto initially to measure content
        listWrapper.style.height = 'auto';
        
        // Calculate available space
        const imageHeight = parentImage.offsetHeight;
        const maxHeight = imageHeight - 100; // Space for button
        
        // Get actual content height
        const contentHeight = listWrapper.scrollHeight;
        
        // Use the smaller of content height or max available height
        const finalHeight = Math.min(contentHeight, maxHeight);
        
        // Set initial height to 0 for animation
        listWrapper.style.height = '0';
        
        requestAnimationFrame(() => {
          // Animate to final height
          listWrapper.style.height = `${finalHeight}px`;
          listWrapper.classList.add('is-open');
          
          // Enable scrolling if content is taller than available space
          if (contentHeight > maxHeight) {
            listWrapper.style.overflowY = 'auto';
          }
        });
      }, 10);
      
      listWrapper.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'height') return;
        listWrapper.removeEventListener('transitionend', handler);
        listWrapper.classList.remove('transitioning');
      }, { once: true });
    }
    
    closeDesktopList(button, listWrapper) {
      button.classList.remove('active');
      listWrapper.style.height = `${listWrapper.offsetHeight}px`;
      
      requestAnimationFrame(() => {
        listWrapper.style.height = '0';
        listWrapper.classList.remove('is-open');
      });
      
      listWrapper.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'height') return;
        listWrapper.removeEventListener('transitionend', handler);
        listWrapper.classList.remove('transitioning');
        listWrapper.classList.add('hidden');
      }, { once: true });
    }
    
    openMobileList(button, listWrapper, overlay, exitButton) {
      // First remove any height constraints
      listWrapper.style.height = 'auto';
      
      // Simple scroll lock
      document.body.style.overflow = 'hidden';
      
      // Remove hidden classes
      listWrapper.classList.remove('hidden');
      listWrapper.classList.add('mobile-view');
      overlay.classList.remove('hidden');
      exitButton.classList.remove('hidden');
      button.classList.add('active');
      
      // Force reflow before adding active classes
      void listWrapper.offsetWidth;
      
      requestAnimationFrame(() => {
        overlay.classList.add('active');
        exitButton.classList.add('active');
        listWrapper.classList.add('is-open');
        
        listWrapper.addEventListener('transitionend', function handler(e) {
          if (e.propertyName !== 'transform') return;
          listWrapper.removeEventListener('transitionend', handler);
          listWrapper.classList.remove('transitioning');
        }, { once: true });
      });
    }
    
    closeMobileList(button, listWrapper, overlay, exitButton) {
      button.classList.remove('active');
      overlay.classList.remove('active');
      exitButton.classList.remove('active');
      listWrapper.classList.remove('is-open');
      
      // Restore scrolling
      document.body.style.overflow = '';
      
      // Force hiding after animation completes
      const forceHide = () => {
        listWrapper.classList.remove('transitioning');
        listWrapper.classList.remove('mobile-view');
        listWrapper.classList.add('hidden');
        overlay.classList.add('hidden');
        exitButton.classList.add('hidden');
      };
      
      // Set a backup timeout in case the transition event doesn't fire
      const timeout = setTimeout(forceHide, 500);
      
      listWrapper.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'transform') return;
        clearTimeout(timeout);
        listWrapper.removeEventListener('transitionend', handler);
        forceHide();
      }, { once: true });
    }
  }
  
  document.addEventListener('DOMContentLoaded', () => {
    new ProductListToggle();
  });